import { Link } from "react-router-dom";
import { CLINIC, CREDENTIALS, PREVIOUS_ROLES, STATS } from "@/data/clinic";
import { GraduationCap, Award, Stethoscope, BookOpen, ArrowRight } from "lucide-react";
import SEOHead, { physicianSchema, localBusinessSchema } from "@/components/SEOHead";

const EXPERTISE = [
  { heading: "Diabetes Care", points: ["Type 1 Diabetes", "Type 2 Diabetes", "Prediabetes", "Gestational Diabetes", "Insulin Therapy", "Diabetes Complications"] },
  { heading: "Endocrinology", points: ["Thyroid Disorders", "Hormonal Imbalance", "PCOS", "Obesity & Metabolic Disorders", "Pituitary Disorders", "Adrenal Disorders"] },
  { heading: "Preventive Nephrology", points: ["Diabetic Kidney Disease", "Kidney Health Monitoring in Diabetes", "Cardio-Diabetes-Renal Care"] },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <SEOHead
        title="About Dr. Ashutosh Mishra | Senior Diabetologist & Endocrinologist Varanasi"
        description="About Dr. Ashutosh Mishra — MBBS, MD (IMS BHU), DFID (CMC Vellore), DMSc Endocrinology (UK). 20+ years treating diabetes, thyroid, PCOS, obesity & hormonal disorders in Varanasi."
        path="/about-dr-ashutosh-mishra"
        breadcrumbs={[
          { name: "Home", url: "/" },
          { name: "About Dr. Ashutosh Mishra", url: "/about-dr-ashutosh-mishra" },
        ]}
        schemas={[
          { id: "ld-physician", data: physicianSchema() },
          { id: "ld-localbiz", data: localBusinessSchema() },
        ]}
      />

      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 text-xs text-slate-500">
        <Link to="/" className="hover:text-green-700">Home</Link> <span className="mx-1.5">/</span> <span className="text-slate-700">About Dr. Ashutosh Mishra</span>
      </nav>

      {/* Hero */}
      <section className="bg-gradient-to-br from-green-50/70 via-white to-green-50/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="text-xs uppercase tracking-[0.22em] text-green-700 font-medium">About the doctor</div>
            <h1 className="font-poppins font-semibold text-4xl sm:text-5xl lg:text-6xl text-slate-900 mt-3 leading-tight">
              About {CLINIC.doctorName}
            </h1>
            <div className="text-slate-600 mt-3 text-lg">{CLINIC.fullTitle}</div>
            <div className="mt-1 text-green-700 font-medium">{CLINIC.subSpeciality}</div>
            <p className="mt-6 text-slate-600 text-lg leading-relaxed">
              {CLINIC.doctorName} is a senior Diabetologist and Endocrinologist based in Varanasi with nearly two decades of experience managing diabetes, thyroid disorders, obesity, hormonal disorders, and metabolic diseases. He has received advanced training from prestigious institutions including <strong>IMS BHU</strong>, <strong>CMC Vellore</strong>, and the <strong>University of South Wales (UK)</strong>.
            </p>
            <p className="mt-4 text-slate-600 leading-relaxed">
              He is known for his comprehensive approach to diabetes care, endocrine disorders, obesity management, and prevention of diabetes-related kidney complications. His clinic in Kabir Nagar, Varanasi has helped thousands of patients regain metabolic control and lead healthier, fuller lives.
            </p>
            <Link to="/contact" data-testid="about-book-btn" className="inline-flex items-center gap-2 mt-8 bg-green-700 hover:bg-green-800 text-white rounded-full px-7 py-3.5 font-medium text-sm shadow">
              Book Consultation <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="lg:col-span-5">
            <div className="relative bg-white rounded-[2rem] border border-slate-100 shadow-xl overflow-hidden">
              <img src={CLINIC.doctorPhoto} alt={CLINIC.doctorName} className="w-full h-[520px] object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="border-y border-slate-100 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid grid-cols-2 sm:grid-cols-4 gap-8 text-center">
          {STATS.map((s) => (
            <div key={s.label}>
              <div className="font-poppins font-semibold text-3xl sm:text-4xl text-green-700">{s.value}</div>
              <div className="text-xs text-slate-500 mt-1 uppercase tracking-wider">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Qualifications */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12">
          <div>
            <h2 className="font-poppins font-semibold text-3xl text-slate-900 flex items-center gap-3">
              <GraduationCap className="w-7 h-7 text-green-700" /> Qualifications
            </h2>
            <ul className="mt-6 space-y-3">
              {CREDENTIALS.map((c) => (
                <li key={c.label} className="flex gap-3 items-start">
                  <div className="mt-2 w-1.5 h-1.5 bg-green-700 rounded-full shrink-0" />
                  <span className="text-slate-700">{c.label}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h2 className="font-poppins font-semibold text-3xl text-slate-900 flex items-center gap-3">
              <Award className="w-7 h-7 text-green-700" /> Previous Academic & Clinical Associations
            </h2>
            <ul className="mt-6 space-y-3">
              {PREVIOUS_ROLES.map((r) => (
                <li key={r} className="flex gap-3 items-start">
                  <div className="mt-2 w-1.5 h-1.5 bg-green-700 rounded-full shrink-0" />
                  <span className="text-slate-700">{r}</span>
                </li>
              ))}
              <li className="text-slate-500 italic text-sm pt-2">Associated with advanced endocrine and diabetes care services in leading institutions.</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Expertise */}
      <section className="bg-green-50/40 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-poppins font-semibold text-3xl sm:text-4xl text-slate-900 flex items-center gap-3">
            <Stethoscope className="w-7 h-7 text-green-700" /> Areas of Expertise
          </h2>
          <div className="mt-10 grid md:grid-cols-3 gap-6">
            {EXPERTISE.map((e) => (
              <div key={e.heading} className="bg-white rounded-3xl border border-slate-100 p-7">
                <h3 className="font-poppins font-semibold text-xl text-slate-900">{e.heading}</h3>
                <ul className="mt-4 space-y-2 text-slate-600 text-sm">
                  {e.points.map((p) => (
                    <li key={p} className="flex gap-2">
                      <span className="text-green-700">•</span>
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Research */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-poppins font-semibold text-3xl sm:text-4xl text-slate-900 flex items-center gap-3">
            <BookOpen className="w-7 h-7 text-green-700" /> Research & Academic Contributions
          </h2>
          <p className="mt-6 text-slate-600 text-lg leading-relaxed">
            {CLINIC.doctorName} has actively participated in research, academic presentations, and medical conferences related to endocrinology, diabetes, metabolism, and internal medicine. He has authored and presented research papers in national and international forums.
          </p>
          <p className="mt-4 text-slate-600 leading-relaxed">
            His clinical philosophy is rooted in evidence-based medicine combined with deep patient empathy — recognising that every metabolic disease has a person and a family behind it.
          </p>
          <Link to="/research-publications" className="inline-flex items-center gap-1.5 mt-6 text-sm text-green-700 font-medium">
            View research & publications <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-slate-950 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-poppins font-semibold text-3xl lg:text-4xl">Ready to consult {CLINIC.doctorName}?</h2>
          <p className="text-slate-300 mt-3">Book your first appointment today — we will guide you through every step.</p>
          <div className="mt-7 flex flex-wrap gap-3 justify-center">
            <Link to="/contact" data-testid="about-cta-book" className="bg-green-500 hover:bg-green-400 text-slate-900 rounded-full px-7 py-3.5 font-medium text-sm">Book Appointment</Link>
            <a href={`tel:+91${CLINIC.phone}`} className="border border-white/30 hover:bg-white/10 rounded-full px-7 py-3.5 font-medium text-sm">Call {CLINIC.phoneDisplay}</a>
          </div>
        </div>
      </section>
    </div>
  );
}
